import { apiDelete, apiGet, apiPost, apiPut } from "./api-service";

// Checklist Template Services
export async function getChecklistTemplates(params?: any) {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `checklist-template?${queryParams}` : "checklist-template";
    const response = await apiGet("core", url);
    return response;
  } catch (e) {
    console.error("Error fetching checklist templates:", e);
    throw e;
  }
}

export async function getChecklistTemplate(id: string) {
  try {
    const response = await apiGet("core", `checklist-template/${id}`);
    return response;
  } catch (e) {
    console.error("Error fetching checklist template:", e);
    throw e;
  }
}

export async function createChecklistTemplate(templateData: any) {
  try {
    const response = await apiPost("core", "checklist-template", templateData);
    return response;
  } catch (e) {
    console.error("Error creating checklist template:", e);
    throw e;
  }
}

export async function updateChecklistTemplate(id: string, templateData: any) {
  try {
    const response = await apiPut("core", `checklist-template/${id}`, templateData);
    return response;
  } catch (e) {
    console.error("Error updating checklist template:", e);
    throw e;
  }
}

export async function deleteChecklistTemplate(id: string) {
  try {
    const response = await apiDelete("core", `checklist-template/${id}`);
    return response;
  } catch (e) {
    console.error("Error deleting checklist template:", e);
    throw e;
  }
}

export async function customizeChecklistTemplate(
  masterTemplateId: string,
  clientId: string,
  customizations?: any
) {
  try {
    const response = await apiPost("core", `checklist-template/${masterTemplateId}/customize`, {
      clientId,
      customizations,
    });
    return response;
  } catch (e) {
    console.error("Error customizing checklist template:", e);
    throw e;
  }
}

// Checklist Instance Services
export async function createChecklistInstance(instanceData: any) {
  try {
    const response = await apiPost("core", "checklist", instanceData);
    return response;
  } catch (e) {
    console.error("Error creating checklist instance:", e);
    throw e;
  }
}

export async function assignTemplateToUser(
  templateId: string,
  userId: string,
  clientId?: string,
  siteId?: string
) {
  try {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 7); // Set due date to one week from now

    const instanceData = {
      template_id: templateId,
      assigned_to: userId,
      due_date: dueDate.toISOString(),
      client_id: clientId,
      site_id: siteId,
      status: "pending",
    };

    const response = await createChecklistInstance(instanceData);
    return response;
  } catch (e) {
    console.error("Error assigning template to user:", e);
    throw e;
  }
}

export async function getMyChecklistInstances(filter?: string) {
  try {
    const params = filter ? `?filter=${filter}` : "";
    const response = await apiGet("core", `checklist/my-instances${params}`);
    return response;
  } catch (e) {
    console.error("Error fetching my checklist instances:", e);
    throw e;
  }
}

export async function getChecklistInstance(id: string) {
  try {
    const response = await apiGet("core", `checklist/instance/${id}`);
    return response;
  } catch (e) {
    console.error("Error fetching checklist instance:", e);
    throw e;
  }
}

export async function startChecklistInstance(id: string) {
  try {
    const response = await apiPut("core", `checklist/instance/${id}`, { operation: "start" });
    return response;
  } catch (e) {
    console.error("Error starting checklist instance:", e);
    throw e;
  }
}

export async function saveChecklistProgress(id: string, data: any) {
  try {
    const response = await apiPut("core", `checklist/instance/${id}`, {
      operation: "save_progress",
      data,
    });
    return response;
  } catch (e) {
    console.error("Error saving checklist progress:", e);
    throw e;
  }
}

export async function completeChecklistInstance(id: string, data: any) {
  try {
    const response = await apiPut("core", `checklist/instance/${id}`, {
      operation: "complete",
      data,
    });
    return response;
  } catch (e) {
    console.error("Error completing checklist instance:", e);
    throw e;
  }
}

// Dashboard and Reporting Services
export async function getChecklistDashboard(params?: any) {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `checklist/dashboard?${queryParams}` : "checklist/dashboard";
    const response = await apiGet("core", url);
    return response;
  } catch (e) {
    console.error("Error fetching checklist dashboard:", e);
    throw e;
  }
}

export async function getComplianceStatus(clientId?: string, siteId?: string) {
  try {
    const params = new URLSearchParams();
    if (clientId) params.append("clientId", clientId);
    if (siteId) params.append("siteId", siteId);

    const queryString = params.toString();
    const url = queryString ? `checklist-compliance?${queryString}` : "checklist-compliance";
    const response = await apiGet("core", url);
    return response;
  } catch (e) {
    console.error("Error fetching compliance status:", e);
    throw e;
  }
}

export async function triggerChecklistEvaluation() {
  try {
    const response = await apiPost("core", "checklist-trigger", { action: "evaluate_all" });
    return response;
  } catch (e) {
    console.error("Error triggering checklist evaluation:", e);
    throw e;
  }
}

// Submission Services
export async function getChecklistSubmissions(params?: any) {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `checklist/submissions?${queryParams}` : "checklist/submissions";
    const response = await apiGet("core", url);
    return response;
  } catch (e) {
    console.error("Error fetching checklist submissions:", e);
    throw e;
  }
}

export async function getChecklistSubmission(id: string) {
  try {
    const response = await apiGet("core", `checklist/submission/${id}`);
    return response;
  } catch (e) {
    console.error("Error fetching checklist submission:", e);
    throw e;
  }
}

export async function createChecklistSubmission(submissionData: any) {
  try {
    const response = await apiPost("core", "checklist-submission", submissionData);
    return response;
  } catch (e) {
    console.error("Error creating checklist submission:", e);
    throw e;
  }
}

// Follow-up Tasks Services
export async function getFollowUpTasks(params?: any) {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams
      ? `checklist/follow-up-tasks?${queryParams}`
      : "checklist/follow-up-tasks";
    const response = await apiGet("core", url);
    return response;
  } catch (e) {
    console.error("Error fetching follow-up tasks:", e);
    throw e;
  }
}

export async function updateFollowUpTask(id: string, data: any) {
  try {
    const response = await apiPut("core", `checklist/follow-up-task/${id}`, data);
    return response;
  } catch (e) {
    console.error("Error updating follow-up task:", e);
    throw e;
  }
}

export async function getChecklistCompliance(siteId: string) {
  try {
    const response = await apiGet("core", `checklist-compliance?site_id=${siteId}`);
    return response;
  } catch (e) {
    console.error("Error fetching checklist compliance:", e);
    throw e;
  }
}
